import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { getApiHeaders, debugApiResponse } from '../../utils/apiUtils';

// Mock data for development
const MOCK_MICROSITES = [
  { id: 'mock-site-1', name: 'Demo Site 1' },
  { id: 'mock-site-2', name: 'Demo Site 2' },
  { id: 'mock-site-3', name: 'Demo Site 3' }
];

const MOCK_PAGES = {
  'mock-site-1': [
    { id: 'mock-page-1', title: 'Home Page' },
    { id: 'mock-page-2', title: 'About Us' }
  ],
  'mock-site-2': [
    { id: 'mock-page-3', title: 'Services' },
    { id: 'mock-page-4', title: 'Contact' }
  ],
  'mock-site-3': [
    { id: 'mock-page-5', title: 'Products' }
  ]
};

const PageBuilderDialog = ({ open, onClose, initialMicrosite = '', accountId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [microsites, setMicrosites] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedMicrosite, setSelectedMicrosite] = useState(initialMicrosite);
  const [selectedPage, setSelectedPage] = useState('');
  const [useMockData, setUseMockData] = useState(false);

  // Update selectedMicrosite when initialMicrosite changes
  useEffect(() => {
    if (initialMicrosite) {
      setSelectedMicrosite(initialMicrosite);
    }
  }, [initialMicrosite]);

  // Fetch microsites when dialog opens
  useEffect(() => {
    if (open && accountId) {
      fetchMicrosites();
    } else if (open && !accountId) {
      console.warn('No accountId provided to PageBuilderDialog, using mock data');
      setMicrosites(MOCK_MICROSITES);
      setUseMockData(true);
    }
  }, [open, accountId]);

  // Fetch pages when a microsite is selected
  useEffect(() => {
    if (selectedMicrosite && accountId) {
      fetchPages(selectedMicrosite);
    } else if (selectedMicrosite && !accountId) {
      console.warn('No accountId provided to PageBuilderDialog, using mock pages');
      const mockPages = MOCK_PAGES[selectedMicrosite] || [];
      setPages(mockPages);
      setUseMockData(true);
    } else {
      setPages([]);
      setSelectedPage('');
    }
  }, [selectedMicrosite, accountId]);

  const fetchMicrosites = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching microsites for account:', accountId);
      
      // Use the account-specific endpoint
      const response = await fetch(`/api/accounts/${accountId}/microsites`, {
        headers: getApiHeaders()
      });
      
      // Debug the response
      const debug = await debugApiResponse(response);
      console.log('Microsites response debug:', debug);
      
      // Check if we got a valid response
      if (response.ok && response.headers.get('content-type')?.includes('application/json')) {
        const data = await response.json();
        console.log('Fetched microsites:', data);
        
        if (Array.isArray(data) && data.length > 0) {
          setMicrosites(data);
          setUseMockData(false);
        } else {
          console.warn('API returned empty array, using mock data');
          setMicrosites(MOCK_MICROSITES);
          setUseMockData(true);
        }
      } else {
        console.warn('API returned non-JSON or error, using mock data');
        setMicrosites(MOCK_MICROSITES);
        setUseMockData(true);
      }
    } catch (err) {
      console.error('Error fetching microsites:', err);
      console.warn('Using mock data due to error');
      setMicrosites(MOCK_MICROSITES);
      setUseMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async (micrositeId) => {
    setLoading(true);
    setError(null);
    
    // If we're using mock data, return mock pages
    if (useMockData) {
      console.log('Using mock pages for microsite:', micrositeId);
      const mockPages = MOCK_PAGES[micrositeId] || [];
      setPages(mockPages);
      setLoading(false);
      return;
    }
    
    try {
      console.log('Fetching pages for microsite:', micrositeId);
      
      // Use the account-specific endpoint
      const response = await fetch(`/api/accounts/${accountId}/microsites/${micrositeId}/pages`, {
        headers: getApiHeaders()
      });
      
      // Debug the response
      const debug = await debugApiResponse(response);
      console.log('Pages response debug:', debug);
      
      // Check if we got a valid response
      if (response.ok && response.headers.get('content-type')?.includes('application/json')) {
        const data = await response.json();
        console.log('Fetched pages:', data);
        setPages(data);
      } else {
        console.warn('API returned non-JSON or error for pages, using mock data');
        const mockPages = MOCK_PAGES[micrositeId] || [];
        setPages(mockPages);
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
      console.warn('Using mock pages due to error');
      const mockPages = MOCK_PAGES[micrositeId] || [];
      setPages(mockPages);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrositeChange = (event) => {
    setSelectedMicrosite(event.target.value);
  };

  const handlePageChange = (event) => {
    setSelectedPage(event.target.value);
  };

  const handleEditPage = () => {
    if (selectedMicrosite && selectedPage) {
      if (accountId) {
        navigate(`/account/${accountId}/microsites/${selectedMicrosite}/pages/${selectedPage}/edit`);
      } else {
        navigate(`/account/microsites/${selectedMicrosite}/pages/${selectedPage}/edit`);
      }
      onClose();
    }
  };

  // Add option to create a new page
  const handleCreateNewPage = async () => {
    if (!selectedMicrosite) return;
    
    setLoading(true);
    setError(null);
    
    // If we're using mock data or missing accountId, simulate creating a page
    if (useMockData || !accountId) {
      console.log('Creating mock page for microsite:', selectedMicrosite);
      
      // Generate a mock page ID
      const mockPageId = `mock-page-${Date.now()}`;
      
      // Navigate to the page builder
      if (accountId) {
        navigate(`/account/${accountId}/microsites/${selectedMicrosite}/pages/${mockPageId}/edit`);
      } else {
        navigate(`/account/microsites/${selectedMicrosite}/pages/${mockPageId}/edit`);
      }
      onClose();
      setLoading(false);
      return;
    }
    
    try {
      // Create a new blank page using the account-specific endpoint
      console.log('Creating new page for microsite:', selectedMicrosite);
      
      const response = await fetch(`/api/accounts/${accountId}/microsites/${selectedMicrosite}/pages`, {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'New Page',
          slug: `new-page-${Date.now()}`,
          content: JSON.stringify({
            content: [],
            root: {
              props: {
                style: {
                  backgroundColor: "#ffffff"
                }
              }
            }
          }),
          accountId
        })
      });
      
      if (response.ok && response.headers.get('content-type')?.includes('application/json')) {
        const newPage = await response.json();
        console.log('Created new page:', newPage);
        
        // Navigate to the page builder for the new page
        navigate(`/account/${accountId}/microsites/${selectedMicrosite}/pages/${newPage.id}/edit`);
      } else {
        console.warn('API returned non-JSON or error when creating page, using mock page');
        
        // Generate a mock page ID
        const mockPageId = `mock-page-${Date.now()}`;
        
        // Navigate to the page builder
        navigate(`/account/${accountId}/microsites/${selectedMicrosite}/pages/${mockPageId}/edit`);
      }
      
      onClose();
      
    } catch (err) {
      console.error('Error creating page:', err);
      setError('Failed to create page. Using mock page instead.');
      
      // Generate a mock page ID
      const mockPageId = `mock-page-${Date.now()}`;
      
      // Navigate to the page builder
      setTimeout(() => {
        if (accountId) {
          navigate(`/account/${accountId}/microsites/${selectedMicrosite}/pages/${mockPageId}/edit`);
        } else {
          navigate(`/account/microsites/${selectedMicrosite}/pages/${mockPageId}/edit`);
        }
        onClose();
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Page Builder
        {useMockData && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            Using demo data - API connection failed
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ minHeight: '200px' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                <InputLabel>Select a Microsite</InputLabel>
                <Select
                  value={selectedMicrosite}
                  onChange={handleMicrositeChange}
                  label="Select a Microsite"
                >
                  {microsites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedMicrosite && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select a Page</InputLabel>
                    <Select
                      value={selectedPage}
                      onChange={handlePageChange}
                      label="Select a Page"
                      disabled={pages.length === 0}
                    >
                      {pages.map((page) => (
                        <MenuItem key={page.id} value={page.id}>
                          {page.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {pages.length === 0 && !loading && (
                    <Typography color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                      No pages found for this microsite.
                    </Typography>
                  )}
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handleCreateNewPage}
                    disabled={!selectedMicrosite}
                  >
                    Create New Page
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleEditPage} 
          variant="contained" 
          color="primary"
          disabled={!selectedMicrosite || !selectedPage}
        >
          Edit with Page Builder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageBuilderDialog; 